import styles from "./Account.module.scss";
import { PUBLIC_ICON, PUBLIC_IMAGE } from "../../constants";
import classNames from "classnames";
import MyInput from "../../components/UI/MyInput/MyInput";
import ReCAPTCHA from "react-google-recaptcha";
import formStore from "../../stores/formStore";
import MyButton from "../../components/UI/MyButton/MyButton";
import { Helmet } from "react-helmet";
import { Context } from "../../..";
import { useNavigate } from "react-router";
import {
  ACCOUNT_RESET_PASSWORD_ROUTE,
  PRESALE_EXCHANGE_ROUTE,
  PROFILE_ROUTE,
  REGISTER_ROUTE_WITH_REF,
} from "../../utils/consts";
import {
  auth,
  check,
  createUser,
  fetchUserInfo,
  sendCodeResetPassword,
  sendEmailResetPassword,
  sendNewResetPassword,
} from "../../http/userAPI";
import { CreateUserParams } from "../../types/types";
import presaleStore from "../../stores/presaleStore";
import { useContext, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import ResetPasswordForm from "./ResetPasswordForm/ResetPasswordForm";

const Account = () => {
  const length = 8;
  const isLocalDevelopment = ["localhost", "127.0.0.1"].includes(
    window.location.hostname
  );

  const [activeTab, setActiveTab] = useState("login");

  const handleCaptchaChange = (token: string | null) => {
    presaleStore.setCaptchaToken(token);
  };

  const { user } = useContext(Context);
  const navigate = useNavigate();
  const [isSending, setIsSending] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [loginTwoFactorCode, setLoginTwoFactorCode] = useState("");
  const [passwordRegister, setPasswordRegister] = useState("");
  const [passwordRegisterRepeat, setPasswordRegisterRepeat] = useState("");
  const [isAuth, setIsAuth] = useState(true);
  const [show, setShow] = useState(false);
  const [showReg, setShowReg] = useState(false);
  const [showRegRep, setShowRegRep] = useState(false);
  const handleClickPassword = () => setShow(!show);
  const handleClickPasswordReg = () => setShowReg(!showReg);
  const handleClickPasswordRegRep = () => setShowRegRep(!showRegRep);
  const [isValid, setIsValid] = useState<boolean | undefined>(false);
  const [isUserRegistr, setIsUserRegistr] = useState<boolean | undefined>(
    false
  );
  const [errorText, setErrorText] = useState<string>("");
  const [ref, setRef] = useState<string>("");
  const [stepsResetPassword, setStepsResetPassword] = useState<number>(1);
  const [code, setCode] = useState<string[]>(Array(length).fill(""));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const verifyAuth = async () => {
      const isAuthenticated = await check();
      if (isAuthenticated) {
        if (presaleStore.isClickBuy) {
          const userInfo = await fetchUserInfo();
          await user.setUser(userInfo);
          await user.setIsAuth(true);
          navigate(PRESALE_EXCHANGE_ROUTE);
        } else {
          const userInfo = await fetchUserInfo();
          await user.setUser(userInfo);
          await user.setIsAuth(true);
          navigate(PROFILE_ROUTE);
        }
      }
    };
    verifyAuth();
  }, [user, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refFromUrl = params.get("ref") || "";
    setRef(refFromUrl);

    if (refFromUrl || window.location.pathname === REGISTER_ROUTE_WITH_REF) {
      setActiveTab("register");
    }
  }, []);

  const login = async () => {
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isPasswordValid = password.trim().length > 0;

    if (!isEmailValid || !isPasswordValid) {
      setIsValid(true);
      return;
    }

    setIsValid(false);

    try {
      let data;
      data = await auth(email, password, loginTwoFactorCode);
      await user.setUser(data);
      await user.setIsAuth(true);
      await user.getUserInfo();
      if (presaleStore.isClickBuy) {
        navigate(PRESALE_EXCHANGE_ROUTE);
        await user.setIsLoading(true);
        await presaleStore.sendDataPresale();
        await user.setIsLoading(false);
      } else {
        navigate(PROFILE_ROUTE);
      }

      setIsAuth(true);
    } catch (e) {
      if (e instanceof Error && (e as Error & {status?:number}).status === 428) {
        setTwoFactorRequired(true);
        setErrorText("Enter the code from your authenticator app or a recovery code.");
        setIsValid(false);
        return;
      }
      setIsValid(true);
      setIsAuth(false);
      if (e instanceof Error && (e as any).response?.data?.message) {
        alert((e as any).response.data.message);
      }
    }
  };

  const sendNewPassword = async () => {
    const isPasswordValid = passwordRegister.length >= 8;

    if (passwordRegister !== passwordRegisterRepeat) {
      setErrorText("Passwords don't match");
      setIsValid(true);
      return;
    }

    if (!isPasswordValid) {
      setErrorText("The password must contain at least 8 characters.");
      setIsValid(true);
      return;
    }

    setIsValid(false);

    try {
      await sendNewResetPassword(email, code.join(""), passwordRegister);

      setEmail("");
      setPasswordRegister("");
      setPasswordRegisterRepeat("");
      setErrorText("");

      setActiveTab("login");
    } catch (e) {
      setIsValid(true);
      setErrorText("Invalid or expired code");
    }
  };

  const register = async () => {
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isPasswordValid = passwordRegister.length >= 8;
    const isUsernameValid = username.trim().length > 0;

    if (passwordRegister !== passwordRegisterRepeat) {
      setErrorText("Passwords don't match");
      setIsValid(true);
      return;
    }

    if (!username || !email || !passwordRegister || !passwordRegisterRepeat) {
      setErrorText("Not all fields are filled in");
      setIsValid(true);
      return;
    }

    if (!isEmailValid) {
      setIsValid(true);
      setErrorText("Enter the correct email address");
      return;
    }

    if (!isPasswordValid) {
      setErrorText("The password must contain at least 8 characters.");
      setIsValid(true);
      return;
    }

    if (!isUsernameValid) {
      setIsValid(true);
      return;
    }

    setIsValid(false);

    try {
      const userParams: CreateUserParams = {
        username: username,
        email: email,
        password: passwordRegister,
        ref: ref,
      };

      const userInfo = await createUser(userParams);

      if (
        typeof userParams.email === "string" &&
        typeof userParams.password === "string"
      ) {
        const data = await auth(userParams.email, userParams.password);
        await user.setUser(data);
        await user.setIsAuth(true);
        await user.getUserInfo();

        if (presaleStore.isClickBuy) {
          navigate(PRESALE_EXCHANGE_ROUTE);
          await user.setIsLoading(true);
          await presaleStore.sendDataPresale();
          await user.setIsLoading(false);
        } else {
          navigate(PROFILE_ROUTE);
        }
      } else {
        setErrorText("The email or password is incorrect.");
        return;
      }

      await setIsAuth(true);
    } catch (e: any) {
      setIsUserRegistr(true);
      setIsAuth(false);

      if (e.status === 400) {
        setErrorText(e.data?.detail || "Error during registration");
      } else if (e.status === 500) {
        setErrorText("Couldn't register, please try again later");
      } else {
        setErrorText("An unknown error has occurred");
      }
    }
  };

  const sendEmail = async () => {
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!isEmailValid) {
      setIsValid(true);
      setErrorText("Enter the correct email address");
      return;
    }

    try {
      let data;
      await sendEmailResetPassword(email);
      setStepsResetPassword(2);
      setErrorText("");
    } catch (e) {
      setIsValid(true);
      setErrorText("No user with this email");
    }
  };

  const sendCode = async () => {
    try {
      await sendCodeResetPassword(email, code.join(""));
      setStepsResetPassword(3);
      setErrorText("");
    } catch (e) {
      setIsValid(true);
      setErrorText(
        "Invalid or expired code. If you requested another code, use the newest email."
      );
    }
  };

  const getMaskedEmail = (email: string): string => {
    const [localPart, domain] = email.split("@");
    if (!localPart || !domain) return email;
    const visible = localPart.slice(0, Math.min(2, localPart.length));
    return `${visible}${"•".repeat(Math.max(3, localPart.length - visible.length))}@${domain}`;
  };

  const handleChangeCode = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newValues = [...code];
    newValues[index] = value;
    setCode(newValues);

    if (value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!text) return;

    const newValues = [...code];
    for (let i = 0; i < length; i++) {
      newValues[i] = text[i] || "";
    }
    setCode(newValues);

    const lastIndex = Math.min(text.length - 1, length - 1);
    inputsRef.current[lastIndex]?.focus();
  };

  const handleSendCode = async () => {
    if (isSending) return;

    setIsSending(true);
    setErrorText("");

    try {
      await sendEmail();
    } catch (e) {
      setErrorText("Failed to send code");
      setIsValid(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Account</title>
        <link rel="icon" href={PUBLIC_ICON + "Alfa.svg"} />
      </Helmet>
      <div className={styles.account__wrapper}>
        <div className={styles.account__img}>
          <img src={PUBLIC_IMAGE + "account__img.svg"} />
        </div>
        <div className={styles.account__form}>
          <div className={styles.account__formHeader}>
            <div
              className={classNames(styles.account__formHeader__login, {
                [styles.selected]: activeTab === "login",
              })}
              onClick={() => {
                setActiveTab("login");
                setIsValid(false);
              }}
            >
              Log in
            </div>
            <div
              className={classNames(styles.account__formHeader__register, {
                [styles.selected]: activeTab === "register",
              })}
              onClick={() => {
                setActiveTab("register");
                setIsValid(false);
              }}
            >
              Sign Up
            </div>
            <div className={styles.information__line}>
              <div className={styles.information__lightBox}></div>
              <div className={styles.main__informationLogo}>
                <img src={PUBLIC_IMAGE + "Main-logoImage.svg"} alt="" />
              </div>
            </div>
          </div>
          <div className={styles.account__formBody}>
            {activeTab === "login" ? (
              <div className={styles.account__formBody__login}>
                <div className={styles.account__formBody__loginTile}>
                  Please provide your E-mail address and Password to log in.
                </div>
                <div
                  className={
                    isValid
                      ? styles.account__formBody__loginTileError
                      : styles.account__formBody__loginTileErrorNone
                  }
                >
                  Check the correctness of the entered data
                </div>
                {twoFactorRequired && errorText && <div className={styles.account__formBody__loginTileError}>{errorText}</div>}
                <div className={styles.account__formBody__loginEmail}>
                  <div className={styles.account__formBody__loginPreText}>
                    {" "}
                    Email{" "}
                  </div>
                  <MyInput
                    onChange={(e) => setEmail(e.target.value)}
                    placeHolder="Enter your email"
                    type="email"
                    value={email}
                  ></MyInput>
                </div>
                <div className={styles.account__formBody__loginPassword}>
                  <div className={styles.account__formBody__loginPreText}>
                    {" "}
                    Password{" "}
                  </div>
                  <MyInput
                    placeHolder="Enter your Password"
                    type={show ? "text" : "password"}
                    onChange={(e) => setPassword(e.target.value)}
                  // value={password}
                  ></MyInput>
                  <div
                    className={styles.account__formBody__loginPasswordImg}
                    onClick={handleClickPassword}
                  >
                    <img
                      src={
                        PUBLIC_IMAGE + "account__formBody__loginPasswordImg.svg"
                      }
                    />
                  </div>
                </div>
                {twoFactorRequired && <div className={styles.account__formBody__loginEmail}>
                  <div className={styles.account__formBody__loginPreText}>Two-factor code</div>
                  <MyInput
                    placeHolder="6-digit or recovery code"
                    type="text"
                    value={loginTwoFactorCode}
                    onChange={(e) => setLoginTwoFactorCode(e.target.value.trim().slice(0, 25))}
                  />
                </div>}
                <div className={styles.account__formBody__loginFooter}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div
                      className={styles.account__formBody__loginFooterRegister}
                      onClick={() => setActiveTab("register")}
                    >
                      Don’t have an account?
                    </div>
                    <div
                      className={styles.account__formBody__loginFooterRegister}
                      onClick={() => setActiveTab("resetPassword")}
                    >
                      Forgot password?
                    </div>
                  </div>
                  <div className={styles.form__reCapcha}>
                    {isLocalDevelopment ? (
                      <div className={styles.localCaptchaNotice}>
                        Local preview · production captcha is disabled
                      </div>
                    ) : (
                      <ReCAPTCHA
                        sitekey="6LffE8IqAAAAAM3RixTIrXWK--794V01rKbaJCio"
                        onChange={handleCaptchaChange}
                        theme="light"
                      />
                    )}
                  </div>
                </div>
                <div
                  className={styles.account__formBody__loginFooterLoginButton}
                >
                  {" "}
                  <MyButton onClick={login}>Log in</MyButton>
                </div>
              </div>
            ) : activeTab === "register" ? (
              <div className={styles.account__formBody__register}>
                <div className={styles.account__formBody__loginTile}>
                  Please provide your E-mail address and Password to log in.
                </div>
                {errorText && (
                  <div className={styles.account__formBody__loginTileError}>
                    {errorText}
                  </div>
                )}
                <div className={styles.account__formBody__loginEmail}>
                  <div className={styles.account__formBody__loginPreText}>
                    {" "}
                    Email{" "}
                  </div>
                  <MyInput
                    onChange={(e) => setEmail(e.target.value)}
                    placeHolder="Enter your email"
                    type="email"
                  ></MyInput>
                </div>
                <div className={styles.account__formBody__loginEmail}>
                  <div className={styles.account__formBody__loginPreText}>
                    {" "}
                    Username{" "}
                  </div>
                  <MyInput
                    onChange={(e) => setUsername(e.target.value)}
                    placeHolder="Enter your username"
                    value={username}
                    type="text"
                  ></MyInput>
                </div>
                <div className={styles.account__formBody__loginPassword}>
                  <div className={styles.account__formBody__loginPreText}>
                    {" "}
                    Password{" "}
                  </div>
                  <MyInput
                    placeHolder="Enter your Password"
                    type={showReg ? "text" : "password"}
                    onChange={(e) => setPasswordRegister(e.target.value)}
                  ></MyInput>
                  <div
                    className={styles.account__formBody__loginPasswordImg}
                    onClick={handleClickPasswordReg}
                  >
                    <img
                      src={
                        PUBLIC_IMAGE + "account__formBody__loginPasswordImg.svg"
                      }
                    />
                  </div>
                </div>
                <div className={styles.account__formBody__loginPassword}>
                  <div className={styles.account__formBody__loginPreText}>
                    {" "}
                    Repeat Password{" "}
                  </div>
                  <MyInput
                    placeHolder="Your password here"
                    type={showRegRep ? "text" : "password"}
                    onChange={(e) => setPasswordRegisterRepeat(e.target.value)}
                  ></MyInput>
                  <div
                    className={styles.account__formBody__loginPasswordImg}
                    onClick={handleClickPasswordRegRep}
                  >
                    <img
                      src={
                        PUBLIC_IMAGE + "account__formBody__loginPasswordImg.svg"
                      }
                    />
                  </div>
                </div>
                <div className={styles.account__formBody__loginFooter}>
                  <div
                    className={styles.account__formBody__loginFooterRegister}
                    onClick={() => setActiveTab("login")}
                  >
                    Do you have an account?
                  </div>
                  <div className={styles.form__reCapcha}>
                    {isLocalDevelopment ? (
                      <div className={styles.localCaptchaNotice}>
                        Local preview · registration captcha is disabled
                      </div>
                    ) : (
                      <ReCAPTCHA
                        sitekey="6LffE8IqAAAAAM3RixTIrXWK--794V01rKbaJCio"
                        onChange={handleCaptchaChange}
                        theme="light"
                      />
                    )}
                  </div>
                </div>
                <div
                  className={styles.account__formBody__loginFooterLoginButton}
                >
                  <MyButton onClick={register}>Register</MyButton>
                </div>
              </div>
            ) : stepsResetPassword === 1 ? (
              <div className={styles.resetPasswordForm}>
                <div className={styles.account__formBody__ResetTitle}>
                  Password Recovery
                </div>
                <div className={styles.account__formBody__loginEmail}>
                  <div className={styles.account__formBody__loginPreText}>
                    Enter the email linked to your account:
                  </div>
                  <div className={styles.account__formBody__resetPasswordInput}>
                    <MyInput
                      onChange={(e) => setEmail(e.target.value)}
                      placeHolder="Enter your email"
                      type="email"
                    ></MyInput>
                  </div>{" "}
                  {!errorText && (
                    <div
                      className={
                        isValid
                          ? styles.account__formBody__loginTileError
                          : styles.account__formBody__loginTileErrorNone
                      }
                    >
                      error: no user with this email
                    </div>
                  )}
                  {errorText && (
                    <div className={styles.account__formBody__loginTileError}>
                      {errorText}
                    </div>
                  )}
                </div>
                <div>
                  <div
                    className={
                      styles.account__formBody__resetPasswordFooterRegister
                    }
                    onClick={() => setActiveTab("register")}
                  >
                    Don’t have an account?
                  </div>
                  <div
                    className={styles.account__formBody__loginFooterBlock}
                  ></div>
                </div>

                <div className={styles.account__formBody__resetPasswordButton}>
                  <MyButton onClick={handleSendCode} disabled={isSending}>
                    {isSending ? "Sending..." : "Send code"}
                  </MyButton>
                </div>
                <div
                  className={
                    styles.account__formBody__resetPasswordFooterWrapper
                  }
                >
                  <div
                    className={styles.account__formBody__resetPasswordFooter}
                    onClick={() => setActiveTab("login")}
                  >
                    ← Back to Login
                  </div>
                </div>
              </div>
            ) : stepsResetPassword === 2 ? (
              <div className={styles.resetPasswordForm}>
                <div className={styles.account__formBody__ResetTitle}>
                  Verification Code
                </div>
                <div className={styles.account__formBody__ResetInfo}>
                  We sent a verification code to: <br></br>{" "}
                  <strong>{getMaskedEmail(email)}</strong>
                  <span className={styles.resetCodeHint}>
                    Use the code from the newest email. A newly requested code
                    replaces every previous one.
                  </span>
                </div>
                <div className={styles.account__formBody__loginEmail}>
                  <div className={styles.account__formBody__loginPreText}>
                    Enter the code:
                  </div>
                  <div className={styles.account__formBody__resetCodeInputs}>
                    {Array.from({ length }).map((_, i) => (
                      <input
                        key={i}
                        ref={(el) => (inputsRef.current[i] = el)}
                        className={styles.account__formBody__resetCodeField}
                        maxLength={1}
                        value={code[i]}
                        onChange={(e) => handleChangeCode(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        onPaste={handlePaste}
                      />
                    ))}
                  </div>{" "}
                  {errorText && (
                    <div className={styles.account__formBody__loginTileError}>
                      {errorText}
                    </div>
                  )}
                </div>
                <div
                  className={
                    styles.account__formBody__resetPasswordFooterRegister
                  }
                  onClick={() => setActiveTab("register")}
                >
                  Don’t have an account?
                </div>
                <div className={styles.account__formBody__resetPasswordButton}>
                  <MyButton
                    onClick={async () => {
                      await sendCode();
                    }}
                  >
                    Verify code
                  </MyButton>
                </div>
                <div
                  className={
                    styles.account__formBody__resetPasswordFooterWrapper
                  }
                >
                  <div
                    className={styles.account__formBody__resetPasswordFooter}
                    onClick={() => setActiveTab("login")}
                  >
                    ← Back to Login
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.resetPasswordForm}>
                <div className={styles.account__formBody__ResetTitle}>
                  New Password
                </div>
                <div className={styles.account__formBody__loginPassword}>
                  <div className={styles.account__formBody__loginPreText}>
                    {" "}
                    Enter new password:
                  </div>
                  <MyInput
                    placeHolder="Enter your Password"
                    type={showReg ? "text" : "password"}
                    onChange={(e) => setPasswordRegister(e.target.value)}
                  ></MyInput>
                  <div
                    className={styles.account__formBody__loginPasswordImg}
                    onClick={handleClickPasswordReg}
                  >
                    <img
                      src={
                        PUBLIC_IMAGE + "account__formBody__loginPasswordImg.svg"
                      }
                    />
                  </div>
                </div>
                <div className={styles.account__formBody__loginPassword}>
                  <div className={styles.account__formBody__loginPreText}>
                    {" "}
                    Confirm new password:
                  </div>
                  <MyInput
                    placeHolder="Your password here"
                    type={showRegRep ? "text" : "password"}
                    onChange={(e) => setPasswordRegisterRepeat(e.target.value)}
                  ></MyInput>
                  <div
                    className={styles.account__formBody__loginPasswordImg}
                    onClick={handleClickPasswordRegRep}
                  >
                    <img
                      src={
                        PUBLIC_IMAGE + "account__formBody__loginPasswordImg.svg"
                      }
                    />
                  </div>
                </div>
                {errorText && (
                  <div className={styles.account__formBody__loginTileError}>
                    {errorText}
                  </div>
                )}
                <div
                  className={
                    styles.account__formBody__resetPasswordFooterRegister
                  }
                  onClick={() => setActiveTab("register")}
                >
                  Don’t have an account?
                </div>
                <div className={styles.account__formBody__resetPasswordButton}>
                  <MyButton
                    onClick={async () => {
                      await sendNewPassword();
                    }}
                  >
                    Login
                  </MyButton>
                </div>
                <div
                  className={
                    styles.account__formBody__resetPasswordFooterWrapper
                  }
                >
                  <div
                    className={styles.account__formBody__resetPasswordFooter}
                    onClick={() => setActiveTab("login")}
                  >
                    ← Back to Login
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(Account);
